// ⚠️ FILE SINH TỰ ĐỘNG — ĐỪNG SỬA TAY.
//
// Nguồn: cnv-server-shared/src/business-logic/leaveBalance.js
// (kernel dùng chung Core + Work SaaS + Connect SaaS). Sinh lại sau khi sửa kernel:
//     node tools/check-leave-balance-drift.js --write
// Gate trong deploy.sh sẽ đỏ nếu file này lệch với kernel.
//
// Vì sao phải sinh: cnv-ui chạy trong trình duyệt (ESM) nên không require được gói BE
// @cnv/server-shared. Chép tay thì ô «Phép còn lại» chặn một con số mà máy chủ tính ra con số
// khác — đúng thứ đã xảy ra 28/09/2026 (Buổi chiều bị tính 1 ngày).

/**
 * Leave balance calculator — pure logic CNV.
 *
 * Rule:
 *   - Chỉ NV "official" (employment_status) mới có phép
 *   - 1 ngày phép/tháng làm việc CHÍNH THỨC, cộng vào NGÀY CỘNG PHÉP của tháng đó (cài đặt
 *     `annual_leave_accrual_day`, mặc định ngày 1) — xem khối «CHÍNH SÁCH CỘNG PHÉP HẰNG THÁNG».
 *   - Mốc cộng phép = ngày HĐLĐ chính thức (official_since) nếu có; fallback join_date.
 *     → tháng thử việc KHÔNG cộng phép (rule 2b, dùng chung Core + SaaS).
 *   - Năm trước → full 12 tháng (nếu vào trước năm trước)
 *   - Năm hiện tại → đến tháng hiện tại nếu hôm nay đã tới ngày cộng, không thì tới tháng trước
 *   - Năm tương lai → 0
 *   - No mốc → default 12 (năm trước) / currentMonth (năm hiện tại)
 *   - Có ngày nghỉ việc (offboarding_date) → cộng tới tháng nghỉ việc rồi dừng; tháng nghỉ việc chỉ
 *     được cộng khi làm đủ `annual_leave_last_month_min_days` ngày (0 = luôn cộng); năm sau năm nghỉ → 0.
 *
 * Audit 2026-05-23: Core có ở `controllers/leaveBalanceController.js`,
 * SaaS thiếu service tính (routes có nhưng API trả null). Đẩy vào shared.
 * 2026-06-24: thêm official_since (mốc HĐ chính thức) — Core hết drift, SaaS dùng chung.
 * 2026-09-28: thêm offboarding_date (người đã LÊN LỊCH nghỉ vẫn đi làm tới ngày nghỉ) và
 *   `leaveRequestDays` — MỘT luật "đơn xin bao nhiêu ngày" cho ô cảnh báo trên form (web + app),
 *   chốt chặn lúc gửi đơn và số ngày TRỪ PHÉP lúc duyệt. Trước đó ô cảnh báo + chốt gửi đếm theo
 *   LỊCH (30/09→30/09 = 1 ngày) còn lúc duyệt lại đọc ô "Thời gian nghỉ" (Buổi chiều = 0,5) ⇒ còn
 *   0,5 ngày phép mà xin nghỉ buổi chiều bị chặn "thiếu 0,5 ngày".
 *
 * ⚠️ File này được SINH sang FE (cnv-ui `src/utils/leaveBalancePolicy.js`, lệnh sinh:
 * `node tools/check-leave-balance-drift.js --write` trong cnvwork-saas). Giữ THUẦN: không
 * require, không API riêng của Node — chạy được cả trong trình duyệt.
 */

/**
 * {năm, tháng 1..12, ngày|null} của một mốc ngày. Chuỗi 'YYYY-MM-DD…' đọc thẳng từ chữ —
 * `new Date('2026-10-01')` là nửa đêm UTC, sang máy lệch múi giờ âm thành 30/09 ⇒ lệch tháng.
 * @returns {{y:number,m:number,d:(number|null)}|null}
 */
function ymdOf(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'string') {
    const hit = /^(\d{4})-(\d{2})(?:-(\d{2}))?/.exec(v);
    if (hit) return { y: Number(hit[1]), m: Number(hit[2]), d: hit[3] ? Number(hit[3]) : null };
  }
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return { y: d.getFullYear(), m: d.getMonth() + 1, d: d.getDate() };
}

function daysInMonth(y, m) {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

// ── CHÍNH SÁCH CỘNG PHÉP HẰNG THÁNG (cài đặt của từng workspace) ─────────────────────────────
// Sếp Phú 09/10/2026, luật của CNV: nhân viên chính thức được cộng 1 ngày phép vào ĐẦU mỗi tháng; tháng có
// ngày làm việc cuối (nghỉ việc) mà làm chưa đủ 15 ngày thì tháng đó KHÔNG cộng. Trước đó HR tính tay theo
// luật riêng trong khi máy cộng theo luật khác, không ai nhìn thấy máy đang chạy luật gì ⇒ HR trừ lương
// những đơn «Nghỉ có lương» mà app đã cho qua. Hai con số giờ là CÀI ĐẶT của workspace (trang Quản lý phép
// năm › Chính sách), không chép cứng trong mã:
//   annual_leave_accrual_day          ngày trong tháng cộng phép của tháng đó: 1..28, 31 = ngày cuối tháng
//                                     (tháng ngắn hơn thì lấy ngày cuối của tháng đó).
//   annual_leave_last_month_min_days  tháng có ngày làm việc cuối: số ngày lịch đã làm trong tháng (từ ngày 1,
//                                     hoặc từ ngày bắt đầu nếu vào cùng tháng, tới hết ngày làm việc cuối) ít
//                                     hơn số này ⇒ tháng đó không cộng. 0 = luôn cộng.
// Thiếu khoá ⇒ đúng cách tính trước 09/10/2026 (ngày 1, luôn cộng tháng nghỉ việc) — khách khác không đổi gì.
const ACCRUAL_SETTING_KEYS = Object.freeze({
  accrualDay: 'annual_leave_accrual_day',
  lastMonthMinDays: 'annual_leave_last_month_min_days',
});
const ACCRUAL_LAST_DAY_OF_MONTH = 31;
const ACCRUAL_POLICY_DEFAULTS = Object.freeze({ accrualDay: 1, lastMonthMinDays: 0 });

function intIn(v, min, max) {
  if (v == null || v === '' || typeof v === 'boolean') return null;
  const n = Number(typeof v === 'string' ? v.trim() : v);
  return Number.isInteger(n) && n >= min && n <= max ? n : null;
}

/**
 * Chính sách cộng phép đã chuẩn hoá. Nhận `{ accrualDay, lastMonthMinDays }` hoặc thẳng bản đồ cài đặt
 * (`{ annual_leave_accrual_day, annual_leave_last_month_min_days, … }`); giá trị hỏng ⇒ mặc định.
 * @returns {{ accrualDay:number, lastMonthMinDays:number }}
 */
function normalizeAccrualPolicy(p) {
  const src = p || {};
  const pick = (a, b) => (src[a] !== undefined ? src[a] : src[b]);
  const day = intIn(pick('accrualDay', ACCRUAL_SETTING_KEYS.accrualDay), 1, 31);
  const min = intIn(pick('lastMonthMinDays', ACCRUAL_SETTING_KEYS.lastMonthMinDays), 0, 31);
  return {
    accrualDay: day == null ? ACCRUAL_POLICY_DEFAULTS.accrualDay : day,
    lastMonthMinDays: min == null ? ACCRUAL_POLICY_DEFAULTS.lastMonthMinDays : min,
  };
}

/**
 * Phép được cấp tính tới hôm nay, kèm tháng nghỉ việc bị bỏ (nếu có) để host ghi sổ / giải thích.
 *
 * @param {object} user - { join_date, employment_status, official_since?, offboarding_date? }
 *   official_since = ngày HĐLĐ chính thức đầu tiên (ưu tiên làm mốc cộng phép). Tháng bắt đầu được tính
 *   cả tháng (kể cả vào giữa tháng).
 *   offboarding_date = ngày làm việc cuối (đã lên lịch nghỉ) — cộng tới tháng đó rồi dừng; tháng đó có
 *   được cộng hay không theo `lastMonthMinDays`.
 *   ⚠️ employment_status là trạng thái làm việc THẬT (Core: người mới LÊN LỊCH nghỉ bị gắn cờ
 *   'resigned' ngay — host phải truyền trạng thái theo hợp đồng, xem leaveAccrualProfile của Core).
 * @param {number} year
 * @param {Date} [now=new Date()]
 * @param {object} [policy] - xem normalizeAccrualPolicy
 * @returns {{ days:number, skippedMonth: (null|{year:number, month:number, workedDays:number, minDays:number}) }}
 */
function accrualBreakdown(user, year, now = new Date(), policy) {
  const out = { days: 0, skippedMonth: null };
  if (!user || user.employment_status !== 'official') return out;
  const pol = normalizeAccrualPolicy(policy);

  const cy = now.getFullYear();
  const cm = now.getMonth() + 1;

  // Tháng cuối cùng đã tới ngày cộng phép. Năm trước: đủ 12. Năm hiện tại: tháng này nếu hôm nay đã tới
  // ngày cộng, không thì tới tháng trước. Năm sau: chưa có gì.
  let endMonth;
  if (year < cy) endMonth = 12;
  else if (year === cy) endMonth = now.getDate() >= Math.min(pol.accrualDay, daysInMonth(cy, cm)) ? cm : cm - 1;
  else return out;

  // Mốc cộng phép: ngày HĐ chính thức nếu có, ngược lại ngày vào (grandfather NV cũ chưa nhập HĐ).
  const startRef = user.official_since || user.join_date;
  const start = startRef ? ymdOf(startRef) : null;
  if (startRef && !start) return out;
  if (start && year < start.y) return out;
  const startMonth = start && year === start.y ? start.m : 1;

  const off = ymdOf(user.offboarding_date);
  if (off && year > off.y) return out;
  if (off && year === off.y) {
    let lastMonth = off.m;
    if (pol.lastMonthMinDays > 0 && off.d != null) {
      const firstDay = start && start.y === off.y && start.m === off.m && start.d != null ? start.d : 1;
      const workedDays = Math.max(0, off.d - firstDay + 1);
      if (workedDays < pol.lastMonthMinDays) {
        lastMonth = off.m - 1;
        if (endMonth >= off.m && off.m >= startMonth) {
          out.skippedMonth = { year: off.y, month: off.m, workedDays, minDays: pol.lastMonthMinDays };
        }
      }
    }
    endMonth = Math.min(endMonth, lastMonth);
  }

  out.days = Math.max(0, endMonth - startMonth + 1);
  return out;
}

/**
 * Tính số ngày phép được cấp tính đến hôm nay — `accrualBreakdown(...).days`.
 * @param {object} user
 * @param {number} year
 * @param {Date} [now=new Date()]
 * @param {object} [policy] - chính sách cộng phép của workspace (thiếu ⇒ ngày 1, luôn cộng tháng nghỉ việc)
 * @returns {number}
 */
function calcAllocatedDays(user, year, now = new Date(), policy) {
  return accrualBreakdown(user, year, now, policy).days;
}

/**
 * Số «được cấp» sổ phép nên giữ sau một lượt đọc lại. CHỈ-TĂNG như cũ (không hạ số HR cấp tay, không hạ
 * số cũ khi đổi mốc), trừ MỘT ngoại lệ: tháng có ngày làm việc cuối bị bỏ vì làm chưa đủ N ngày. HR hay
 * LÊN LỊCH nghỉ SAU khi máy đã cộng phép đầu tháng ⇒ sổ đang dư đúng tháng đó. Chỉ hạ khi sổ giữ ĐÚNG số
 * máy tự cộng nếu không có luật này (chưa ai sửa tay), và tháng nghỉ là tháng này hoặc tháng trước (lúc
 * HR chốt công) — không đụng hồ sơ đã khép từ lâu.
 *
 * @param {number|string|null} stored - allocated_days đang lưu; null = chưa có sổ
 * @returns {{ allocated:number, revoked: (null|{year:number, month:number, workedDays:number, minDays:number, from:number, to:number}) }}
 */
function reconcileAllocated(stored, user, year, now = new Date(), policy) {
  const b = accrualBreakdown(user, year, now, policy);
  const cur = stored == null || stored === '' ? NaN : Number(stored);
  if (!Number.isFinite(cur) || b.days >= cur) return { allocated: b.days, revoked: null };
  const sk = b.skippedMonth;
  if (sk) {
    const monthsAgo = (now.getFullYear() - sk.year) * 12 + (now.getMonth() + 1 - sk.month);
    const withoutRule = accrualBreakdown(user, year, now, { ...normalizeAccrualPolicy(policy), lastMonthMinDays: 0 }).days;
    if (monthsAgo >= 0 && monthsAgo <= 1 && withoutRule === cur) {
      return { allocated: b.days, revoked: { ...sk, from: cur, to: b.days } };
    }
  }
  return { allocated: cur, revoked: null };
}

/**
 * Tính remaining = allocated + carried_over + adjusted - used.
 *
 * @param {object} balance - { allocated_days, carried_over_days, adjusted_days, used_days }
 */
function calcRemaining(balance) {
  if (!balance) return 0;
  const allocated = parseFloat(balance.allocated_days) || 0;
  const carriedOver = parseFloat(balance.carried_over_days) || 0;
  const adjusted = parseFloat(balance.adjusted_days) || 0;
  const used = parseFloat(balance.used_days) || 0;
  return allocated + carriedOver + adjusted - used;
}

/**
 * Số ngày nghỉ (calendar, inclusive) từ leave_range {from,to}. Chỉ là phần DỰ PHÒNG của
 * `leaveRequestDays` khi đơn không có ô thời lượng nghỉ. Trả 0 nếu thiếu/không hợp lệ.
 * @param {{from?:string,to?:string}} range
 */
function leaveDaysInRange(range) {
  if (!range || !range.from || !range.to) return 0;
  const a = new Date(range.from);
  const b = new Date(range.to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  // chỉ lấy phần ngày (bỏ giờ) để diff chuẩn
  const da = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const db = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  const days = Math.floor((db - da) / 86400000) + 1; // inclusive
  return days > 0 ? days : 0;
}

// ── Ô THỜI LƯỢNG NGHỈ ────────────────────────────────────────────────────────
// Mẫu mới gắn `_purpose: 'leave_duration'`. Mẫu chuyển từ workflow cũ (Core #21) có ô radio nhãn
// "Thời gian nghỉ" (Buổi sáng · Buổi chiều · 1 ngày · 2 ngày · 3 ngày · 1/4 ngày) KHÔNG có _purpose
// ⇒ nhận theo nhãn. Ô "Khoảng thời gian nghỉ" là leave_range (có _purpose) nên không bị bắt nhầm.
const LEAVE_DURATION_PURPOSE = 'leave_duration';
const LEAVE_DURATION_LABEL_RE = /^(thoi gian nghi|thoi luong nghi|so ngay nghi)(?![a-z])/;

// Chữ thường, bỏ dấu, gộp khoảng trắng — so khớp không phụ thuộc cách gõ (NFC/NFD, hoa/thường).
function foldVi(s) {
  return String(s == null ? '' : s)
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd').replace(/Đ/g, 'D')
    .toLowerCase().replace(/\s+/g, ' ').trim();
}

/** Ô thời lượng nghỉ của mẫu đơn (null nếu mẫu không có). */
function findLeaveDurationField(formFields) {
  const fields = Array.isArray(formFields) ? formFields : [];
  const byPurpose = fields.find((f) => f && f._purpose === LEAVE_DURATION_PURPOSE);
  if (byPurpose) return byPurpose;
  return fields.find((f) => f && !f._purpose && typeof f.id === 'string' && !f.id.startsWith('_')
    && LEAVE_DURATION_LABEL_RE.test(foldVi(f.label))) || null;
}

/**
 * Giá trị ô thời lượng nghỉ → số ngày. "Buổi sáng"/"Buổi chiều"/"Nửa ngày"/"1/2 ngày" = 0,5 ·
 * "1/4 ngày" = 0,25 · "N ngày" = N · "Cả ngày" = 1. Không hiểu được → null.
 */
function parseLeaveDuration(value) {
  if (typeof value === 'number') return Number.isFinite(value) && value > 0 ? value : null;
  if (typeof value !== 'string') return null;
  const s = foldVi(value);
  if (!s) return null;
  if (/^1\/4( ngay)?$/.test(s)) return 0.25;
  if (s.includes('buoi sang') || s.includes('buoi chieu') || s === 'sang' || s === 'chieu'
    || s === 'nua ngay' || /^1\/2( ngay)?$/.test(s)) return 0.5;
  const m = /^(\d+(?:[.,]\d+)?) ?ngay$/.exec(s);
  if (m) {
    const n = parseFloat(m[1].replace(',', '.'));
    return n > 0 ? n : null;
  }
  if (s === 'ca ngay' || s === 'full day') return 1;
  return null;
}

/**
 * Số ngày của GIÁ TRỊ đã chọn ở ô thời lượng. Đơn lưu MÃ của lựa chọn (`o.value`); lựa chọn thêm bằng
 * nút «Thêm tùy chọn» của trình dựng mẫu mang mã máy sinh («opt7»), còn chữ nhân viên nhìn thấy
 * («1,5 ngày») nằm ở NHÃN. Trước 02/10/2026 chỉ đọc mã ⇒ HR thêm «1,5 ngày» thì đơn rơi về đếm theo
 * lịch (14→15/10 = 2 ngày), sửa nhãn «3 ngày» thành «2,5 ngày» thì vẫn trừ 3. Đọc NHÃN trước (đúng thứ
 * đã chọn), không hiểu thì đọc mã.
 */
function leaveDurationOfValue(field, value) {
  const opts = field && Array.isArray(field.options) ? field.options : [];
  const opt = value == null ? null : opts.find((o) => o && o.value === value);
  const byLabel = opt ? parseLeaveDuration(opt.label) : null;
  return byLabel != null ? byLabel : parseLeaveDuration(value);
}

// ── CHỮ CỦA LỰA CHỌN THỜI LƯỢNG (trình dựng mẫu đơn) ─────────────────────────
// HR gõ lựa chọn tay: «1.5», «1 ngày rưỡi», «1 ngày 1 buổi», «2 ngay»… `parseLeaveDuration` chỉ hiểu vài
// dạng; dạng khác thì đơn rơi về đếm theo lịch mà không ai hay. Trình dựng gọi hàm này lúc rời ô để đưa
// chữ về dạng chuẩn. Không nhận ra thì trả lại đúng chữ đã gõ (gọn khoảng trắng).
const DURATION_WORDS = {
  'buoi sang': 'Buổi sáng',
  sang: 'Buổi sáng',
  'buoi chieu': 'Buổi chiều',
  chieu: 'Buổi chiều',
  'nua ngay': 'Nửa ngày',
  '1 buoi': 'Nửa ngày',
  'mot buoi': 'Nửa ngày',
  'ca ngay': 'Cả ngày',
  'full day': 'Cả ngày',
};
const NEAR = (a, b) => Math.abs(a - b) < 1e-9;

function daysLabel(n) {
  if (NEAR(n, 0.25)) return '1/4 ngày';
  if (NEAR(n, 0.5)) return 'Nửa ngày';
  return `${fmtDays(n).replace('.', ',')} ngày`;
}

/** Chữ HR gõ cho một lựa chọn thời lượng → chữ chuẩn («1 ngày rưỡi» → «1,5 ngày»). */
function formatLeaveDurationLabel(text) {
  const raw = String(text == null ? '' : text).replace(/\s+/g, ' ').trim();
  const s = foldVi(raw);
  if (!s) return raw;
  if (DURATION_WORDS[s]) return DURATION_WORDS[s];
  if (/^1\/4( ngay)?$/.test(s)) return '1/4 ngày';
  if (/^1\/2( ngay)?$/.test(s)) return 'Nửa ngày';
  let m = /^(\d+(?:[.,]\d+)?) ?(?:ngay)?$/.exec(s);
  if (m) {
    const n = parseFloat(m[1].replace(',', '.'));
    return n > 0 ? daysLabel(n) : raw;
  }
  // «1 ngày rưỡi» · «1 ngày 1 buổi» · «1 ngày + buổi chiều» · «1 ngày và nửa ngày»
  m = /^(\d+) ?ngay ?(?:ruoi|(?:(?:\+|va|&) ?)?(?:(?:1|mot) )?(?:buoi(?: sang| chieu)?|nua(?: ngay)?))$/.exec(s);
  if (m) return daysLabel(parseInt(m[1], 10) + 0.5);
  m = /^(\d+) ?buoi$/.exec(s);
  if (m) {
    const n = parseInt(m[1], 10) / 2;
    return n > 0 ? daysLabel(n) : raw;
  }
  return raw;
}

/**
 * Bảng công tô KHỚP được số ngày này không. Đơn một ngày: 1/4 · nửa · cả ngày; đơn nhiều ngày chỉ mang
 * phần lẻ NỬA ngày — `timesheetExport.spreadLeaveOverDays` dồn mọi phần lẻ vào ngày đầu thành «/2», nên
 * «1,25 ngày» trừ phép 1,25 mà bảng công tô 1,5.
 */
function leaveDurationFitsTimesheet(n) {
  const v = Number(n);
  if (!(v > 0)) return false;
  return NEAR(v, 0.25) || NEAR(v * 2, Math.round(v * 2));
}

/**
 * SỐ NGÀY MỘT ĐƠN NGHỈ XIN — MỘT luật cho: ô "Phép còn lại" trên form (web + app), chốt chặn
 * lúc gửi đơn (Core + Work + Connect) và số ngày TRỪ PHÉP lúc duyệt.
 *   1. Ô thời lượng nghỉ có giá trị hiểu được → dùng nó (khoảng ngày chỉ nói NGÀY NÀO, không nói
 *      nửa hay cả ngày). Đọc nhãn lựa chọn trước, mã sau — `leaveDurationOfValue`.
 *   2. Không có / không hiểu được → số ngày theo lịch của khoảng nghỉ (tính cả 2 đầu).
 * Chỉ đọc ĐÚNG ô thời lượng — bản quét-mọi-ô cũ vớ nhầm chữ "buổi chiều" trong tiêu đề / lý do
 * chi tiết (đo 28/09/2026 trên 1.192 đơn đã duyệt: 2 đơn "1 ngày" bị trừ 0,5).
 *
 * @param {object} p
 * @param {Array}  p.formFields - form_fields của mẫu
 * @param {object} p.formData   - form_data của đơn
 * @param {{from?:string,to?:string}} [p.range] - khoảng nghỉ đã chuẩn hoá (bỏ trống = đọc ô leave_range)
 * @returns {number}
 */
function leaveRequestDays({ formFields, formData, range } = {}) {
  const fields = Array.isArray(formFields) ? formFields : [];
  const data = formData || {};
  const durField = findLeaveDurationField(fields);
  const dur = durField ? leaveDurationOfValue(durField, data[durField.id]) : null;
  if (dur != null) return dur;
  if (range) return leaveDaysInRange(range);
  const rangeField = fields.find((f) => f && f._purpose === 'leave_range');
  return leaveDaysInRange(rangeField ? data[rangeField.id] : null);
}

// ── BUỔI CỦA ĐƠN NỬA NGÀY ────────────────────────────────────────────────────
// Bảng `leaves` chỉ lưu NGÀY + số ngày (0,5), không lưu sáng hay chiều ⇒ nhãn «Nghỉ phép» cạnh tên ở
// chat treo SUỐT ngày: 07/10/2026 HR xin «Buổi sáng» (APR-2610-074) mà buổi chiều vẫn hiện đang nghỉ.
// Buổi chỉ nằm ở ô thời lượng của lá đơn — host đọc lại đơn qua `external_leave_id` (`approvalIdOfLeave`).

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** `leaves.external_leave_id` dạng `approval:<uuid>` (dòng sinh lúc duyệt đơn) → uuid đơn; khác ⇒ null. */
function approvalIdOfLeave(externalLeaveId) {
  const m = /^approval:(.+)$/.exec(String(externalLeaveId == null ? '' : externalLeaveId));
  return m && UUID_RE.test(m[1]) ? m[1] : null;
}

/**
 * Buổi nghỉ của một đơn: 'morning' | 'afternoon' | null.
 * Chỉ trả buổi khi đơn đúng NỬA ngày và khoảng nghỉ nằm trong MỘT ngày. Còn lại ⇒ null (= cả ngày):
 * cả ngày · 1/4 ngày (không biết giờ nào) · «Nửa ngày» (không nói buổi) · đơn nhiều ngày.
 * Đọc nhãn lựa chọn trước, mã sau — cùng thứ tự với `leaveDurationOfValue`.
 * @param {object} p
 * @param {Array}  p.formFields - form_fields của mẫu
 * @param {object} p.formData   - form_data của đơn
 * @returns {'morning'|'afternoon'|null}
 */
function leaveDayPart({ formFields, formData } = {}) {
  const fields = Array.isArray(formFields) ? formFields : [];
  const data = formData || {};
  const durField = findLeaveDurationField(fields);
  if (!durField) return null;
  const value = data[durField.id];
  if (leaveDurationOfValue(durField, value) !== 0.5) return null;
  const rangeField = fields.find((f) => f && f._purpose === 'leave_range');
  const range = rangeField ? data[rangeField.id] : null;
  if (range && typeof range === 'object' && range.from && range.to
    && String(range.from).slice(0, 10) !== String(range.to).slice(0, 10)) return null;
  const opts = Array.isArray(durField.options) ? durField.options : [];
  const opt = value == null ? null : opts.find((o) => o && o.value === value);
  for (const text of [opt && opt.label, value]) {
    const s = foldVi(typeof text === 'string' ? text : '');
    if (s.includes('buoi sang') || s === 'sang') return 'morning';
    if (s.includes('buoi chieu') || s === 'chieu') return 'afternoon';
  }
  return null;
}

// Định dạng số ngày gọn (bỏ .0 thừa): 1, 0.5, 1.5
function fmtDays(n) {
  const v = Math.round((Number(n) || 0) * 100) / 100;
  return String(v);
}

/**
 * Kiểm tra phép cho đơn nghỉ. CHỈ loại 'paid' (nghỉ có lương — trừ phép) mới tiêu phép;
 * 'unpaid' luôn đủ. Thuần — dùng chung FE (cảnh báo form) + BE submit.
 *
 * @param {object} p
 * @param {number} p.remaining     - số phép còn lại (calcRemaining)
 * @param {number} p.requestedDays - số ngày đơn xin (leaveRequestDays)
 * @param {string} p.leaveType     - 'paid' | 'unpaid'
 * @param {string} [p.policy='warn'] - 'warn' | 'block' | 'auto_unpaid'
 * @returns {{ enough:boolean, level:'ok'|'warn'|'block', shortfall:number,
 *            remaining:number, requestedDays:number, message:(string|null) }}
 */
function checkLeaveBalance({ remaining, requestedDays, leaveType, policy = 'warn' } = {}) {
  const rem = Number(remaining) || 0;
  const req = Number(requestedDays) || 0;
  if (leaveType !== 'paid') {
    return { enough: true, level: 'ok', shortfall: 0, remaining: rem, requestedDays: req, message: null };
  }
  const shortfall = Math.round(Math.max(0, req - rem) * 100) / 100;
  if (shortfall <= 0) {
    return { enough: true, level: 'ok', shortfall: 0, remaining: rem, requestedDays: req, message: null };
  }
  const level = policy === 'block' ? 'block' : 'warn';
  const message = `Không đủ phép: còn ${fmtDays(rem)} ngày, đơn xin ${fmtDays(req)} ngày (thiếu ${fmtDays(shortfall)} ngày).`;
  return { enough: false, level, shortfall, remaining: rem, requestedDays: req, message };
}

/**
 * used_days mới sau khi cộng delta (duyệt: +ngày; hoàn: -ngày). Không âm.
 * @param {object} balance - { used_days }
 * @param {number} deltaDays
 */
function nextUsedDays(balance, deltaDays) {
  const used = parseFloat(balance && balance.used_days) || 0;
  return Math.max(0, Math.round((used + (Number(deltaDays) || 0)) * 100) / 100);
}

export {
  calcAllocatedDays,
  accrualBreakdown,
  reconcileAllocated,
  normalizeAccrualPolicy,
  ACCRUAL_SETTING_KEYS,
  ACCRUAL_POLICY_DEFAULTS,
  ACCRUAL_LAST_DAY_OF_MONTH,
  calcRemaining,
  checkLeaveBalance,
  nextUsedDays,
  leaveDaysInRange,
  leaveRequestDays,
  findLeaveDurationField,
  parseLeaveDuration,
  leaveDurationOfValue,
  formatLeaveDurationLabel,
  leaveDurationFitsTimesheet,
  leaveDayPart,
  approvalIdOfLeave,
  LEAVE_DURATION_PURPOSE,
  fmtDays,
};
